/**
 * @fileoverview One row of the Active Clients grid.
 * @description Tenant ID, bank name and type, location, tier, branches, owner, client-since date,
 * the HR Admin state and the client's status. A row carries no contact value. The whole row and the
 * bank name open the client's details; the name is a real button so the keyboard can reach it too.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import {
  CLIENT_STATUS_BADGE_VARIANT,
  clientInitials,
  dayOf,
  HR_ADMIN_BADGE_VARIANT,
} from '@/features/sales/lib/sales-clients.utils'
import Badge from '@/shared/components/atoms/Badge'

import type { ClientListItem } from '@/features/sales/types/sales-client.types'

/** The grid's column template, shared by the header and every row. */
export const CLIENT_GRID_COLUMNS =
  'grid-cols-[0.9fr_2.1fr_1.3fr_0.8fr_0.7fr_1.2fr_1fr_1.1fr_0.9fr_0.6fr]'

interface ClientRowProps {
  client: ClientListItem
  onView: (client: ClientListItem) => void
}

/**
 * @component
 * @param {ClientRowProps} props - The client and the handler.
 * @param {ClientListItem} props.client - The grid row's client.
 * @param {(client: ClientListItem) => void} props.onView - Opens the client's details.
 */
function ClientRow({ client, onView }: ClientRowProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="client-row"
      onClick={() => onView(client)}
      className={`grid cursor-pointer ${CLIENT_GRID_COLUMNS} items-center gap-3 border-b border-row-border px-4 py-2.5 hover:bg-surface-hover sm:px-5`}
    >
      <span className="text-compact font-semibold text-slate" data-testid="client-id">
        {client.tenantCode}
      </span>
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-primary text-xs font-bold text-white">
          {clientInitials(client.bankName)}
        </div>
        <div className="flex min-w-0 flex-col">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onView(client)
            }}
            data-testid="client-name-button"
            className="min-w-0 cursor-pointer truncate text-left text-compact-lg font-semibold text-ink hover:underline"
          >
            {client.bankName}
          </button>
          <span className="truncate text-xs text-footer" data-testid="client-bank-type">
            {client.bankType.name}
          </span>
        </div>
      </div>
      <span className="truncate text-compact text-cell" data-testid="client-location">
        {client.city}, {client.state.name}
      </span>
      <span className="text-compact font-medium text-cell" data-testid="client-tier">
        {client.tier.name}
      </span>
      <span className="text-compact text-cell" data-testid="client-branches">
        {client.branchCount}
      </span>
      <span className="truncate text-compact text-cell" data-testid="client-owner">
        {client.executive?.name ?? '—'}
      </span>
      <span className="text-compact text-cell" data-testid="client-since">
        {dayOf(client.onboardedAt)}
      </span>
      <Badge variant={HR_ADMIN_BADGE_VARIANT[client.hrAdminState]} data-testid="client-hr-badge">
        {t(`sales.activeClients.hrAdminState.${client.hrAdminState}`)}
      </Badge>
      <Badge
        variant={CLIENT_STATUS_BADGE_VARIANT[client.status]}
        dot
        data-testid="client-status-badge"
      >
        {t(`sales.activeClients.status.${client.status}`)}
      </Badge>
      <div className="text-right">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onView(client)
          }}
          data-testid="client-view-button"
          className="cursor-pointer text-compact font-semibold text-primary hover:underline"
        >
          {t('sales.activeClients.view')}
        </button>
      </div>
    </div>
  )
}

export default ClientRow
